import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { useWallets } from '../../store/wallets';
import { useToast } from '../../store/toast';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { deriveTron, isValidMnemonic } from '../../lib/wallet';
import { ApiError } from '../../lib/api';
import { FigIcon } from '../FigIcon';

const WORDS = 24;

/** One editable, numbered recovery-word cell (matches the seed screen's cells). */
function WordCell({
  n,
  value,
  onChange,
}: {
  n: number;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-[12px] bg-card p-2">
      <span className="w-[22px] shrink-0 text-center text-[13px] text-muted">{n}.</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="min-w-0 flex-1 bg-transparent text-[15px] font-medium text-ink outline-none"
      />
    </div>
  );
}

/** Add-wallet screen (route `wallets`). Every wallet has its own seed phrase:
 *  "Create" mints a fresh one; "Add existing" imports a 12/24-word phrase as a
 *  separate wallet. */
export function CreateWalletScreen() {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const add = useWallets((s) => s.add);
  const setActive = useWallets((s) => s.setActive);
  const wallets = useWallets((s) => s.wallets);
  const showToast = useToast((s) => s.show);
  const [step, setStep] = useState<'choose' | 'import'>('choose');
  const [words, setWords] = useState<string[]>(() => Array(WORDS).fill(''));
  const [busy, setBusy] = useState(false);
  // On the import step, the native BackButton returns to the chooser.
  useBackHandler(step === 'import', () => setStep('choose'));

  const entered = words.map((w) => w.trim().toLowerCase()).filter(Boolean);
  const filled = entered.length === 12 || entered.length === 24;

  // Typing/pasting a phrase into one cell distributes the words across cells.
  const setWord = (i: number, v: string) => {
    const parts = v.trim().split(/\s+/);
    if (parts.length > 1) {
      setWords((prev) => {
        const next = [...prev];
        for (let k = 0; k < parts.length && i + k < WORDS; k++) next[i + k] = parts[k]!;
        return next;
      });
    } else {
      setWords((prev) => prev.map((w, k) => (k === i ? v : w)));
    }
  };

  const addWallet = async (mnemonic?: string) => {
    haptic('medium');
    setBusy(true);
    try {
      const w = await add(undefined, mnemonic);
      setActive(w.id);
      showToast(t('wallet.created'));
      pop();
    } catch (e) {
      showToast(
        t(e instanceof ApiError && e.code === 'wallet_exists' ? 'wallet.alreadyAdded' : 'wallet.createFailed'),
      );
    } finally {
      setBusy(false);
    }
  };

  const createNew = () => {
    if (busy) return;
    void addWallet();
  };

  const importSeed = () => {
    if (!filled || busy) return;
    const phrase = entered.join(' ');
    if (!isValidMnemonic(phrase)) {
      showToast(t('wallet.invalidSeed'));
      return;
    }
    const { address } = deriveTron(phrase, 0);
    if (wallets.some((w) => w.address === address)) {
      showToast(t('wallet.alreadyAdded'));
      return;
    }
    void addWallet(phrase);
  };

  if (step === 'import') {
    return (
      // The fixed import button is a sibling of the push-enter block, not a
      // child: a position:fixed element inside a translateX-animated ancestor
      // rides with that transform for the 0.52s slide and snaps to the viewport
      // bottom when it ends. Kept outside, it's anchored from the first frame.
      <div className="flex min-h-full flex-col bg-surface">
        {/* scrollable content — clears the fixed button with pb */}
        <div className="push-enter flex flex-1 flex-col items-center gap-6 px-4 pb-32 pt-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="grid size-20 place-items-center rounded-full bg-card shadow-card">
              <FigIcon name="pfSeed" size={36} className="text-accent" />
            </span>
            <h1 className="text-[22px] font-medium text-ink">{t('wallet.importTitle')}</h1>
            <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">
              {t('wallet.importSubtitle')}
            </p>
          </div>

          <div className="grid w-full grid-cols-2 gap-x-3 gap-y-3">
            {words.map((w, i) => (
              <WordCell key={i} n={i + 1} value={w} onChange={(v) => setWord(i, v)} />
            ))}
          </div>
        </div>

        {/* fixed import button */}
        <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[440px] bg-gradient-to-t from-surface via-surface px-4 pb-[max(20px,var(--tg-safe-bottom))] pt-4">
          <button
            onClick={importSeed}
            disabled={!filled || busy}
            className={`flex h-[56px] w-full items-center justify-center rounded-full border border-card text-[15px] font-bold transition-colors ${
              filled && !busy ? 'bg-accent text-white active:bg-accent-ink' : 'bg-accent text-white/[0.44]'
            }`}
          >
            {t('wallet.import')}
          </button>
        </div>
      </div>
    );
  }

  // Fixed bottom actions (same pattern as the import step): min-h-screen inside
  // the pt-safe shell used to push the second button below the fold.
  return (
    <div className="page-enter flex min-h-full flex-col bg-surface">
      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 pb-44 pt-6 text-center">
        <span className="grid size-24 place-items-center rounded-[28px] bg-gradient-to-br from-[#5cb6f8] to-[#2152f1] text-white">
          <FigIcon name="pfCard" size={44} />
        </span>
        <div className="flex flex-col items-center gap-2">
          <h1 className="text-[24px] font-medium text-ink">{t('wallet.addTitle')}</h1>
          <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">
            {t('wallet.addSubtitle')}
          </p>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 mx-auto flex max-w-[440px] flex-col gap-3 bg-gradient-to-t from-surface via-surface px-4 pb-[max(24px,var(--tg-safe-bottom))] pt-4">
        <button
          onClick={createNew}
          disabled={busy}
          className="flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:bg-accent-ink disabled:opacity-80"
        >
          {t('wallet.createWallet')}
        </button>
        <button
          onClick={() => {
            haptic();
            setStep('import');
          }}
          disabled={busy}
          className="flex h-[56px] w-full items-center justify-center rounded-full bg-card text-[15px] font-bold text-ink shadow-card active:opacity-80 disabled:opacity-80"
        >
          {t('wallet.addExisting')}
        </button>
      </div>
    </div>
  );
}
